// React y hooks de utilidad
import React, { useMemo } from "react";
import PropTypes from "prop-types"; // Validación de props
import classNames from "classnames"; // Para combinar clases condicionalmente
import { useLocation } from "react-router-dom"; // Para obtener la ruta actual

// Componentes de Material-UI
import { makeStyles } from "@material-ui/core/styles"; // Hook para aplicar estilos
import AppBar from "@material-ui/core/AppBar"; // Barra superior
import Toolbar from "@material-ui/core/Toolbar"; // Contenedor de contenido dentro del AppBar
import IconButton from "@material-ui/core/IconButton"; // Botón de ícono
import Hidden from "@material-ui/core/Hidden"; // Mostrar/ocultar contenido según tamaño de pantalla

// Iconos de Material-UI
import FormatIndentDecreaseIcon from "@material-ui/icons/FormatIndentDecrease"; // Icono para "ocultar" sidebar
import FormatIndentIncreaseIcon from "@material-ui/icons/FormatIndentIncrease"; // Icono para "mostrar" sidebar
import MenuIcon from "@material-ui/icons/Menu"; // Icono de menú hamburguesa para móviles

// Componentes personalizados
import AdminNavbarLinks from "../Navbars/AdminNavbarLinks"; // Links del navbar para vista LTR
import RTLNavbarLinks from "../Navbars/RTLNavbarLinks"; // Links del navbar para vista RTL
import Button from "../CustomButtons/Button"; // Botón personalizado

// Estilos
import styles from "../../assets/jss/material-dashboard-react/components/headerStyle.js";

// Hook que genera las clases a partir de los estilos
const useStyles = makeStyles(styles);

// Componente principal del Navbar
export default function Navbar({
  color,                // Color del AppBar (opcional)
  rtlActive,            // Si la vista es RTL (Right to Left)
  handleDrawerToggle,   // Función que abre el sidebar en móviles
  routes,               // Lista de rutas para mostrar el nombre activo
  showSidebar,          // Estado del sidebar (visible o no)
  setShowSidebar        // Función para cambiar el estado del sidebar
}) {
  const classes = useStyles();        // Obtener clases de estilo
  const location = useLocation();     // Obtener la ruta actual del navegador

  // Función que alterna la visibilidad del sidebar
  const toggleSidebar = () => {
    setShowSidebar((prev) => !prev);
  };

  // Obtiene el nombre de la ruta actual a mostrar en la cabecera
  const brandName = useMemo(() => {
    const activeRoute = routes.find((route) =>
      location.pathname.includes(route.layout + route.path)
    );
    return activeRoute ? (rtlActive ? activeRoute.rtlName : activeRoute.name) : "";
  }, [location.pathname, routes, rtlActive]);

  // Combina clases del AppBar y le agrega una clase según el color recibido
  const appBarClasses = classNames(classes.appBar, {
    [classes[color]]: color,
  });

  return (
    <AppBar className={appBarClasses}>
      <Toolbar className={classes.container}>
        <div className={classes.flex}>
          {/* Botón que alterna la visibilidad del sidebar */}
          <Button
            color="transparent"
            onClick={toggleSidebar}
            style={{ borderRadius: 70 }}
          >
            {/* Icono según el estado del sidebar */}
            {showSidebar ? <FormatIndentDecreaseIcon /> : <FormatIndentIncreaseIcon />}
          </Button>
          {/* Nombre de la página/ruta activa */}
          <a color="transparent" className={classes.title}>
            {brandName}
          </a>
        </div>

        {/* Links visibles solo en pantallas grandes */}
        <Hidden smDown implementation="css">
          {rtlActive ? <RTLNavbarLinks /> : <AdminNavbarLinks />}
        </Hidden>

        {/* Botón menú visible solo en pantallas chicas */}
        <Hidden mdUp implementation="css">
          <IconButton color="inherit" aria-label="open drawer" onClick={handleDrawerToggle}>
            <MenuIcon />
          </IconButton>
        </Hidden>
      </Toolbar>
    </AppBar>
  );
}

// Validación de props para mayor seguridad
Navbar.propTypes = {
  color: PropTypes.oneOf(["primary", "info", "success", "warning", "danger"]),
  rtlActive: PropTypes.bool,
  handleDrawerToggle: PropTypes.func,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
  showSidebar: PropTypes.bool.isRequired,
  setShowSidebar: PropTypes.func.isRequired,
};
